import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trophy } from 'lucide-react';
import { useCurrentUser } from '@/lib/use-current-user';
import { useTournaments } from '@/data/use-tournaments';
import { AppHeader, EmptyState, LoadingState, ErrorState } from '@/components/design';
import { TournamentCard } from '@/components/design/tournament-card';
import type { TournamentStatus } from '@/data/types';

type Filter = 'all' | TournamentStatus;

const FILTERS: { k: Filter; label: string }[] = [
  { k: 'all', label: 'Все' },
  { k: 'IN_PROGRESS', label: 'Идут' },
  { k: 'READY', label: 'Готовы' },
  { k: 'DRAFT', label: 'Черновики' },
  { k: 'COMPLETED', label: 'Завершены' },
];

export function TournamentsList() {
  const user = useCurrentUser();
  const { tournaments, error } = useTournaments(user?.uid);
  const [filter, setFilter] = useState<Filter>('all');

  return (
    <div style={{ minHeight: '100vh' }}>
      <AppHeader showUser />
      <div className="container-d" style={{ paddingTop: 32, paddingBottom: 64 }}>
        <div
          className="row gap-4"
          style={{ justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap' }}
        >
          <div>
            <h1 className="td-h1" style={{ marginBottom: 4 }}>
              Мои турниры
            </h1>
            <div className="t-small t-mute">
              Создавайте и ведите турниры — от черновика до итогов
            </div>
          </div>
          <Link to="/tournaments/new" className="btn btn-primary">
            <Plus size={16} /> Создать турнир
          </Link>
        </div>

        {error && (
          <ErrorState
            title="Не удалось загрузить турниры"
            body={error.message}
          />
        )}

        {!error && tournaments === undefined && <LoadingState label="Загружаем турниры..." />}

        {!error && tournaments && tournaments.length === 0 && (
          <div className="card-d" style={{ padding: 0 }}>
            <EmptyState
              icon={Trophy}
              title="Пока нет турниров"
              body="Создайте первый турнир — это займёт меньше минуты."
              action={
                <Link to="/tournaments/new" className="btn btn-primary">
                  <Plus size={16} /> Создать первый турнир
                </Link>
              }
            />
          </div>
        )}

        {!error && tournaments && tournaments.length > 0 && (
          <>
            <div className="row gap-3" style={{ marginBottom: 20, flexWrap: 'wrap' }}>
              <div className="seg">
                {FILTERS.map(({ k, label }) => (
                  <button
                    key={k}
                    aria-selected={filter === k}
                    onClick={() => setFilter(k)}
                  >
                    {label}
                    <span
                      className="t-mono t-soft"
                      style={{ marginLeft: 6, fontSize: 11 }}
                    >
                      {k === 'all'
                        ? tournaments.length
                        : tournaments.filter((t) => t.status === k).length}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 16,
              }}
            >
              {tournaments
                .filter((t) => filter === 'all' || t.status === filter)
                .map((t) => (
                  <TournamentCard key={t.id} tournament={t} />
                ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
